import type { CommentItem } from '@nolens/domain';
import { formatDateTime } from '@nolens/i18n';
import { Button, cx, useLocale } from '@nolens/ui';
import { useState, type FormEvent } from 'react';
import { useCommentActions } from '../lib/queries';
import { componentsText } from '../i18n/components';

/** Comentários de um passo (stepId) ou do guia (stepId null). */
export function CommentThread({
  guideId,
  stepId,
  comments,
  canModerate,
  compact = false,
}: {
  guideId: string;
  stepId: string | null;
  comments: CommentItem[];
  canModerate: boolean;
  compact?: boolean;
}) {
  const locale = useLocale();
  const t = componentsText(locale);
  const act = useCommentActions(guideId);
  const [open, setOpen] = useState(!compact);
  const [body, setBody] = useState('');
  const list = comments.filter((c) => c.stepId === stepId);
  const unresolved = list.filter((c) => !c.resolvedAt).length;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    act.add.mutate({ body: body.trim(), stepId }, { onSuccess: () => setBody('') });
  };
  if (!open)
    return (
      <button
        type="button"
        className="no-print mt-3 text-[13px] text-text-3 hover:text-text-1"
        onClick={() => setOpen(true)}
        data-comments={list.length}
      >
        {list.length ? t.comments(list.length, unresolved) : t.addComment}
      </button>
    );
  return (
    <div className="no-print mt-3 rounded-[12px] bg-surface-2 p-3" data-thread={stepId ?? 'guide'}>
      <ul className="flex flex-col gap-2.5">
        {list.map((c) => (
          <li key={c.id} className={cx('text-[14px]', c.resolvedAt && 'opacity-60')}>
            <div className="flex items-baseline gap-2 text-[12.5px] text-text-3">
              <span className="font-medium text-text-2">{c.authorEmail ?? t.someone}</span>
              <span>{formatDateTime(locale, c.createdAt)}</span>
              {c.resolvedAt && <span>{t.resolved}</span>}
              <span className="ml-auto flex gap-2">
                {(canModerate || c.mine) && (
                  <button
                    type="button"
                    className="hover:text-text-1"
                    onClick={() => act.resolve.mutate({ id: c.id, resolved: !c.resolvedAt })}
                  >
                    {c.resolvedAt ? t.reopen : t.resolve}
                  </button>
                )}
                {(canModerate || c.mine) && (
                  <button
                    type="button"
                    className="hover:text-critical"
                    onClick={() => act.remove.mutate(c.id)}
                  >
                    {t.delete}
                  </button>
                )}
              </span>
            </div>
            <p className="mt-0.5 whitespace-pre-wrap">{c.body}</p>
          </li>
        ))}
      </ul>
      <form onSubmit={submit} className={cx('flex gap-2', list.length && 'mt-3')}>
        <input
          aria-label={stepId ? t.commentStep : t.commentGuide}
          className="h-9 min-w-0 flex-1 rounded-[10px] bg-surface-1 px-3 text-[14px] shadow-[inset_0_0_0_1px_var(--border)] outline-none focus:shadow-[inset_0_0_0_2px_var(--accent)]"
          placeholder={t.commentPlaceholder}
          maxLength={2000}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <Button size="sm" type="submit" loading={act.add.isPending} disabled={!body.trim()}>
          {t.send}
        </Button>
      </form>
    </div>
  );
}
